import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { S } from "../../styles";

export function DemandEntry({ onSuccess }: { onSuccess: () => void }) {
  const queryClient = useQueryClient();

  const { data: partsData } = useQuery({
    queryKey: ["parts"],
    queryFn: api.listParts,
    staleTime: 5 * 60_000,
  });
  const parts = partsData?.items ?? [];

  const [selectedPartId, setSelectedPartId] = useState("");
  const [qty, setQty] = useState("1");
  const [cond, setCond] = useState("NE");
  const [reqBy, setReqBy] = useState("");
  const [isAog, setIsAog] = useState(false);
  const [station, setStation] = useState("");
  const [workOrderRef, setWorkOrderRef] = useState("");
  const [notes, setNotes] = useState("");

  // Use selected or fall back to first loaded part
  const partId = selectedPartId || parts[0]?.id || "";

  const submitMutation = useMutation({
    mutationFn: () =>
      api.createRequirement({
        part_id: partId,
        quantity: parseInt(qty, 10),
        condition_code: cond,
        required_by_date: reqBy || undefined,
        is_aog: isAog,
        station: station || undefined,
        work_order_ref: workOrderRef || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      setQty("1");
      setCond("NE");
      setReqBy("");
      setIsAog(false);
      setStation("");
      setWorkOrderRef("");
      setNotes("");
      setSelectedPartId("");
      void queryClient.invalidateQueries({ queryKey: ["rfqs"] });
      setTimeout(onSuccess, 1500);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMutation.mutate();
  };

  return (
    <div style={S.card}>
      <h2 style={S.h2}>New Demand Entry</h2>
      <p style={S.muted}>
        Submit a requirement to automatically dispatch an RFQ to suppliers.
      </p>
      <form onSubmit={handleSubmit}>
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Part *</label>
            <select
              style={S.select}
              value={partId}
              onChange={(e) => setSelectedPartId(e.target.value)}
              required
            >
              {parts.length === 0 && <option value="">Loading parts…</option>}
              {parts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.part_number} — {p.description ?? "no description"}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={S.label}>Quantity *</label>
            <input
              style={S.input}
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              required
            />
          </div>
        </div>
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Condition Code</label>
            <select
              style={S.select}
              value={cond}
              onChange={(e) => setCond(e.target.value)}
            >
              <option value="NE">NE — New</option>
              <option value="OH">OH — Overhauled</option>
              <option value="SV">SV — Serviceable</option>
              <option value="AR">AR — As Removed</option>
            </select>
          </div>
          <div>
            <label style={S.label}>Required By Date</label>
            <input
              style={S.input}
              type="date"
              value={reqBy}
              onChange={(e) => setReqBy(e.target.value)}
            />
          </div>
        </div>
        <div style={S.grid2}>
          <div>
            <label style={S.label}>Station (IATA code)</label>
            <input
              style={S.input}
              placeholder="e.g. JFK"
              value={station}
              onChange={(e) => setStation(e.target.value)}
            />
          </div>
          <div>
            <label style={S.label}>Work Order Reference</label>
            <input
              style={S.input}
              placeholder="e.g. WO-2026-04321"
              value={workOrderRef}
              onChange={(e) => setWorkOrderRef(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label style={S.label}>Notes</label>
          <input
            style={S.input}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <label
          style={{
            ...S.label,
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <input
            type="checkbox"
            checked={isAog}
            onChange={(e) => setIsAog(e.target.checked)}
          />
          <span
            style={{
              color: isAog ? "#c0392b" : undefined,
              fontWeight: isAog ? 700 : 400,
            }}
          >
            ⚡ Aircraft on Ground (AOG)
          </span>
        </label>
        {submitMutation.error && (
          <p style={S.err}>{submitMutation.error.message}</p>
        )}
        {submitMutation.isSuccess && submitMutation.data && (
          <p style={S.ok}>
            ✓ Requirement {submitMutation.data.id.slice(0, 8)}… created — RFQ
            workflow started automatically.
          </p>
        )}
        <button
          style={S.btn}
          type="submit"
          disabled={submitMutation.isPending || !partId}
        >
          {submitMutation.isPending
            ? "Submitting…"
            : "Submit Requirement → Dispatch RFQ"}
        </button>
      </form>
    </div>
  );
}
